import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { useMemberships } from '../../../../../hooks/useMemberships';
import { useFunzioni } from '../../../../components/Util/featureFlags';
import { conMotivo } from '../../../../components/Util/erroreServer';
import { usePageTitle, useStatoInSolaLettura } from '../../../../layouts/PageHeaderExtra';
import { statoAssemblea } from '../../assemblee/stati';
import { rigaRichiesta } from './riga';

/*
 * Scheda della richiesta di adesione (03/10/2026): «Reinvia», «Accetta», «Rifiuta» e «Cancella» erano nel
 * menu a fine riga della lista Richieste, ora stanno qui. La richiesta arriva dalla lista (navigate state)
 * e si rilegge dalle membership della struttura (useMemberships) cercandola per id.
 */

const LIST_URL = '/struttura/richieste';

export const SchedaRichiesta = () => {
	const { assemblies: ASSEMBLEE_ENABLED } = useFunzioni();
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { memberships, fetchMemberships, invalidateCache } = useMemberships({ autoFetch: true });

	const daLista = location.state?.richiesta || null;
	const richiesta = useMemo(() => {
		const trovata = (memberships || []).find(m => String(m.id) === String(id));
		return trovata ? rigaRichiesta(trovata) : daLista;
	}, [memberships, id, daLista]);

	const [loadingShowed, setLoadingShowed] = useState(false);
	const [errore, setErrore] = useState('');
	// Conferma: { titolo, testo, azione, distruttiva, delibera }
	const [conferma, setConferma] = useState(null);
	const [assemblee, setAssemblee] = useState([]);
	const [assembleaScelta, setAssembleaScelta] = useState('');

	usePageTitle(richiesta?.name);
	const stato = richiesta?.status?.label || '';
	useStatoInSolaLettura(stato, stato === 'Rifiutata' ? 'non-attivo' : stato === 'Confermata' ? 'attivo' : 'bozza');

	// Le sedute proponibili sono quelle che si sono gia' tenute
	useEffect(() => {
		if (!conferma?.delibera || assemblee.length > 0) return;
		const dettagli = sessionStorage.getItem('userDetails');
		if (!dettagli) return;
		new MyApiClient().listAssemblies(JSON.parse(dettagli).idToken, { limit: 50 }).then((risultato) => {
			if (!risultato || risultato.error) return;
			setAssemblee((risultato.items || []).filter(a => a.status !== 'DRAFT' && a.status !== 'CONVENED'));
		});
	}, [conferma, assemblee.length]);

	const caricamentoFinito = memberships !== null && memberships !== undefined;
	if (!richiesta) {
		return (
			<>
				<Card>
					<Card.Body className="sid-card-body--spaced">
						{caricamentoFinito ? 'Richiesta non trovata: potrebbe essere già stata accettata o cancellata.' : 'Caricamento in corso...'}
					</Card.Body>
				</Card>
				<div className="d-flex justify-content-between align-items-center gap-4 mt-4">
					<span />
					<button type="button" className="btn btn-dark light" onClick={() => navigate(LIST_URL)}>Indietro</button>
				</div>
			</>
		);
	}

	const inAttesa = richiesta.status?.label === 'In attesa';
	const inviata = richiesta.invitationType === 'CLUB_TO_USER';
	const ricevuta = richiesta.invitationType === 'USER_TO_CLUB';
	// La delibera si chiede solo per i soci: staff tecnico e consiglio non passano dall'assemblea
	const perSoci = richiesta.membershipRank === 'MEMBER' && ASSEMBLEE_ENABLED;

	const esegui = async (chiamata, messaggioErrore) => {
		const dettagli = sessionStorage.getItem('userDetails');
		if (!dettagli) return;
		const token = JSON.parse(dettagli).idToken;
		setConferma(null);
		setLoadingShowed(true);
		let esito;
		try {
			esito = await chiamata(new MyApiClient(), token);
		} catch (e) {
			esito = null;
		}
		setLoadingShowed(false);
		if (!esito) {
			setErrore(conMotivo(messaggioErrore));
			return;
		}
		// Svuota la cache, altrimenti la lista mostrerebbe i dati vecchi
		invalidateCache();
		fetchMemberships();
		navigate(LIST_URL);
	};

	const reinvia = () => setConferma({
		titolo: 'Reinviare la richiesta?',
		testo: `Reinviare la richiesta a «${richiesta.name}»?`,
		azione: () => esegui((api, token) => api.reinviteUser(token, richiesta.userId, richiesta.membershipRank), "Si è verificato un errore durante il reinvio"),
	});

	const cancella = () => setConferma({
		titolo: 'Cancellare la richiesta?',
		testo: `Cancellare la richiesta inviata a «${richiesta.name}»? L'operazione non si può annullare.`,
		distruttiva: true,
		azione: () => esegui((api, token) => api.deleteMembership(token, richiesta.userId, richiesta.membershipRank, richiesta.sportType), "Si è verificato un errore durante la cancellazione"),
	});

	const accetta = () => {
		setAssembleaScelta('');
		setConferma({
			titolo: richiesta.membershipRank === 'COACH' ? 'Accettazione richiesta inserimento staff tecnico' : richiesta.membershipRank === 'MEMBER' ? 'Ammissione a socio' : 'Accettare la richiesta?',
			testo: `Accettare la richiesta di «${richiesta.name}»?`,
			delibera: perSoci,
			azione: (assemblea) => esegui((api, token) => api.updateMembership(token, richiesta.userId, richiesta.membershipRank, 'CONFIRMED', richiesta.sportType, assemblea || null), "Si è verificato un errore durante l'accettazione"),
		});
	};

	const rifiuta = () => {
		setAssembleaScelta('');
		setConferma({
			titolo: richiesta.membershipRank === 'MEMBER' ? 'Candidatura respinta' : 'Rifiutare la richiesta?',
			testo: `Rifiutare la richiesta di «${richiesta.name}»?`,
			delibera: perSoci,
			azione: (assemblea) => esegui((api, token) => api.updateMembership(token, richiesta.userId, richiesta.membershipRank, 'DECLINED', richiesta.sportType, assemblea || null), "Si è verificato un errore durante il rifiuto"),
		});
	};

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={!!errore} onHide={() => setErrore('')} centered>
				<Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
				<Modal.Body>{errore}</Modal.Body>
			</Modal>

			<Modal className="fade" show={!!conferma} onHide={() => setConferma(null)} centered>
				<Modal.Header closeButton><Modal.Title>{conferma?.titolo}</Modal.Title></Modal.Header>
				<Modal.Body>
					<div>{conferma?.testo}</div>
					{conferma?.delibera && (
						<div className="mt-3">
							<Form.Label htmlFor="delibera">Assemblea che ha deliberato</Form.Label>
							<Form.Select id="delibera" value={assembleaScelta} onChange={(e) => setAssembleaScelta(e.target.value)}>
								<option value="">Non indicata</option>
								{assemblee.map(a => (
									<option key={a.id} value={a.id}>
										{`N. ${a.number}`}
										{a.title ? ` - ${a.title}` : ''}
										{a.firstCall && a.firstCall.date ? ` (${a.firstCall.date})` : ''}
										{` - ${statoAssemblea(a.status).label}`}
									</option>
								))}
							</Form.Select>
							<Form.Text className="text-muted">
								{assemblee.length === 0
									? "Nessuna assemblea si è ancora tenuta: l'esito si registra comunque, e il collegamento al verbale resta vuoto."
									: 'Facoltativa. Indicandola, dal libro soci si risale al verbale che ha deciso.'}
							</Form.Text>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-light btn-add" onClick={() => setConferma(null)}>Annulla</Button>
					<Button variant={conferma?.distruttiva ? 'danger' : 'primary'} onClick={() => conferma?.azione(assembleaScelta)}>
						{conferma?.distruttiva ? 'Cancella' : 'Continua'}
					</Button>
				</Modal.Footer>
			</Modal>

			<Card>
				<Card.Body className="sid-card-body--spaced">
					<h5 className="sid-form-section">Richiesta</h5>
					<Row className="g-4">
						<Col md={6}>
							<Form.Label>Richiedente</Form.Label>
							<div className="sid-readonly-value">{richiesta.name || '—'}</div>
						</Col>
						<Col md={6}>
							<Form.Label>Data richiesta</Form.Label>
							<div className="sid-readonly-value">{richiesta.date || '—'}</div>
						</Col>
						<Col md={6}>
							<Form.Label>Ruolo</Form.Label>
							<div className="sid-readonly-value">{richiesta.type || '—'}</div>
						</Col>
						<Col md={6}>
							<Form.Label>Sport</Form.Label>
							<div className="sid-readonly-value">{richiesta.sport || '—'}</div>
						</Col>
						<Col md={6}>
							<Form.Label>Tipologia</Form.Label>
							<div className="sid-readonly-value">
								{richiesta.verso === 'Ricevuta' ? 'Ricevuta (chiesta dal player)' : 'Inviata (invito della struttura)'}
							</div>
						</Col>
					</Row>
				</Card.Body>
			</Card>

			<div className="d-flex justify-content-between align-items-center gap-4 mt-4">
				{inAttesa && inviata
					? <button type="button" className="btn btn-danger light" onClick={cancella}>Cancella richiesta</button>
					: <span />}
				<div className="d-flex gap-4">
					<button type="button" className="btn btn-dark light" onClick={() => navigate(LIST_URL)}>Indietro</button>
					{inAttesa && inviata && <Button className="btn btn-primary" onClick={reinvia}>Reinvia richiesta</Button>}
					{inAttesa && ricevuta && <button type="button" className="btn btn-dark light" onClick={rifiuta}>Rifiuta</button>}
					{inAttesa && ricevuta && <Button className="btn btn-primary" onClick={accetta}>Accetta</Button>}
				</div>
			</div>
		</>
	);
};

export default SchedaRichiesta;
